import { Button } from "@tokenterminal/core/ui/button/Button"
import { NumberField } from "@tokenterminal/core/ui/input/NumberField"
import { type ReactNode, useEffect, useRef, useState } from "react"
import { Popover } from "react-aria-components"

function isArraySorted(arr: number[]) {
  for (let i = 0; i < arr.length - 1; i++) {
    if (arr[i] > arr[i + 1]) {
      return false
    }
  }
  return true
}

export const AutoWidthNumberInputPopoverSingle = ({
  value,
  onChange,
  autoFocusOnMount,
  clearAutoFocusOnMount,
  valueDisplayFormatter,
  numberInputPrefix,
  numberInputSuffix,
}: {
  value: number | undefined
  onChange: ({
    value,
    debounced,
    persist,
  }: {
    value: number | undefined
    debounced?: boolean
    persist?: boolean
  }) => void
  autoFocusOnMount: boolean
  clearAutoFocusOnMount: () => void
  valueDisplayFormatter: (value: number) => string
  numberInputPrefix?: ReactNode
  numberInputSuffix?: ReactNode
}) => {
  const inputContainerRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  if (!isOpen && autoFocusOnMount) {
    setIsOpen(true)
    setTimeout(() => {
      clearAutoFocusOnMount()
    }, 100)
  }

  useEffect(() => {
    const input = inputContainerRef.current?.querySelector("input")

    if (autoFocusOnMount && isOpen && input) {
      input?.select()
    }
  }, [autoFocusOnMount, clearAutoFocusOnMount, isOpen, inputContainerRef])

  useEffect(() => {
    if (isOpen) {
      inputContainerRef.current?.querySelector("input")?.focus()
    }
  }, [isOpen])

  let displayValue: ReactNode = (
    <span className="text-fg-secondary">Select value</span>
  )
  if (value !== undefined) {
    displayValue = valueDisplayFormatter(value)
  }

  return (
    <>
      <Button
        ref={triggerRef}
        variant="ghost"
        className="!border-none !rounded-none !h-full !px-2 font-normal"
        size="small"
        onPress={() => {
          setIsOpen(true)
        }}
      >
        {displayValue}
      </Button>
      <Popover
        isOpen={isOpen}
        onOpenChange={(nextIsOpen) => {
          if (nextIsOpen) {
            setIsOpen(nextIsOpen)
          } else {
            // When clicking outside the popover, we need to make sure the input
            // value calls onChange, so we manually blur the input before
            // closing the popover
            const activeElement = document.activeElement
            if (
              activeElement instanceof HTMLInputElement &&
              inputContainerRef.current?.contains(activeElement)
            ) {
              activeElement.blur()
            }
            setIsOpen(false)
          }
        }}
        triggerRef={triggerRef}
        placement="bottom"
        className="tt-popover !min-w-0 rounded-[theme(spacing[2.5])] flex flex-col overflow-hidden p-2 !w-[250px]"
      >
        <div ref={inputContainerRef}>
          <NumberField
            aria-label="Number input"
            value={value}
            onChange={(value) => {
              if (Number.isNaN(value)) {
                onChange({ value: undefined, persist: false })
              } else {
                onChange({ value, debounced: true, persist: true })
              }
            }}
            placeholder="Value"
            inputProps={{
              onKeyDown: (e) => {
                if (e.key === "Enter") {
                  const input = e.target as HTMLInputElement
                  input.blur()
                  setIsOpen(false)
                }
              },
            }}
            slots={{
              trailing: numberInputSuffix ? (
                <span className="text-fg-secondary">{numberInputSuffix}</span>
              ) : undefined,
              leading: numberInputPrefix ? (
                <span className="text-fg-secondary">{numberInputPrefix}</span>
              ) : undefined,
            }}
          />
        </div>
      </Popover>
    </>
  )
}

export const AutoWidthNumberInputPopoverRange = ({
  value,
  onChange,
  autoFocusOnMount,
  clearAutoFocusOnMount,
  valueDisplayFormatter,
  numberInputPrefix,
  numberInputSuffix,
}: {
  value: (number | undefined)[] | undefined
  onChange: ({
    value,
    debounced,
    persist,
  }: {
    value: (number | undefined)[] | undefined
    debounced?: boolean
    persist?: boolean
  }) => void
  autoFocusOnMount: boolean
  clearAutoFocusOnMount: () => void
  valueDisplayFormatter: (value: number) => string
  numberInputPrefix?: ReactNode
  numberInputSuffix?: ReactNode
}) => {
  const inputContainerRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  if (!isOpen && autoFocusOnMount) {
    setIsOpen(true)
    setTimeout(() => {
      clearAutoFocusOnMount()
    }, 100)
  }

  useEffect(() => {
    const input = inputContainerRef.current?.querySelector("input")
    if (autoFocusOnMount && isOpen && input) {
      input?.select()
    }
  }, [autoFocusOnMount, clearAutoFocusOnMount, isOpen, inputContainerRef])

  useEffect(() => {
    if (isOpen) {
      inputContainerRef.current?.querySelector("input")?.focus()
    }
  }, [isOpen])

  const pressedEnter = useRef(false)

  const handleChangeMin = (newMinValue: number | undefined) => {
    const newValue = [newMinValue, value?.[1]]

    // We only sort the values if they are all defined
    if (newValue.every((v) => v !== undefined)) {
      newValue.sort((a, b) => a - b)
      onChange({
        value: newValue,
        debounced: true,
        persist: pressedEnter.current || isArraySorted(newValue),
      })
    } else {
      onChange({
        value: newValue,
        debounced: true,
        persist: false,
      })
    }
  }

  const handleChangeMax = (newMaxValue: number | undefined) => {
    const newValue = [value?.[0], newMaxValue]

    // We only sort the values if they are all defined
    if (newValue.every((v) => v !== undefined)) {
      newValue.sort((a, b) => a - b)
      onChange({
        value: newValue,
        debounced: true,
        persist: pressedEnter.current || isArraySorted(newValue),
      })
    } else {
      onChange({
        value: newValue,
        debounced: true,
        persist: false,
      })
    }
    10
  }

  let displayValue: ReactNode = (
    <span className="text-fg-secondary">Select range</span>
  )
  if (value && value.every((v) => v !== undefined)) {
    displayValue = (
      <span>
        {valueDisplayFormatter(value[0])} and{" "}
        {valueDisplayFormatter(value[1] ?? 0)}
      </span>
    )
  }

  return (
    <>
      <Button
        ref={triggerRef}
        variant="ghost"
        className="!border-none !rounded-none !h-full !px-2 font-normal"
        size="small"
        onPress={() => {
          setIsOpen(true)
        }}
      >
        {displayValue}
      </Button>
      <Popover
        isOpen={isOpen}
        onOpenChange={(nextIsOpen) => {
          if (nextIsOpen) {
            setIsOpen(nextIsOpen)
          } else if (!nextIsOpen) {
            // When clicking outside the popover, we need to make sure the input
            // value calls onChange, so we manually blur the input before
            // closing the popover
            const activeElement = document.activeElement
            if (
              activeElement instanceof HTMLInputElement &&
              inputContainerRef.current?.contains(activeElement)
            ) {
              activeElement.blur()
            }
            setIsOpen(false)
          }
        }}
        triggerRef={triggerRef}
        placement="bottom"
        className="tt-popover !min-w-0 rounded-[theme(spacing[2.5])] flex flex-col overflow-hidden p-2 !w-[350px]"
      >
        <div ref={inputContainerRef}>
          <div className="flex items-center gap-2">
            <NumberField
              aria-label="Min"
              value={value?.[0]}
              onChange={(value) => {
                handleChangeMin(Number.isNaN(value) ? undefined : value)
              }}
              inputProps={{
                onKeyDown: (e) => {
                  if (e.key === "Enter") {
                    pressedEnter.current = true
                    ;(e.target as HTMLInputElement).blur()
                    setIsOpen(false)
                  }
                },
              }}
              placeholder="From"
              slots={{
                trailing: numberInputSuffix ? (
                  <span className="text-fg-secondary">{numberInputSuffix}</span>
                ) : undefined,
                leading: numberInputPrefix ? (
                  <span className="text-fg-secondary">{numberInputPrefix}</span>
                ) : undefined,
              }}
            />
            <span className="text-fg-secondary text-sm">to</span>
            <NumberField
              aria-label="Max"
              value={value?.[1]}
              onChange={(value) => {
                handleChangeMax(Number.isNaN(value) ? undefined : value)
              }}
              inputProps={{
                onKeyDown: (e) => {
                  if (e.key === "Enter") {
                    pressedEnter.current = true
                    ;(e.target as HTMLInputElement).blur()
                    setIsOpen(false)
                  }
                },
              }}
              placeholder="To"
              slots={{
                trailing: numberInputSuffix ? (
                  <span className="text-fg-secondary">{numberInputSuffix}</span>
                ) : undefined,
                leading: numberInputPrefix ? (
                  <span className="text-fg-secondary">{numberInputPrefix}</span>
                ) : undefined,
              }}
            />
          </div>
        </div>
      </Popover>
    </>
  )
}
